import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode
} from 'react';
import { useAuth } from './AuthContext';
import { customerApi, depositApi, usageApi, statisticsApi } from '../services/api/endpoints';
import { handleApiError, logError } from '../utils/errorHandler';
import { useToast } from '../hooks/useToast';
import logger from '../utils/logger';
import type {
  Customer,
  CustomerCreate,
  CustomerUpdate,
  Deposit,
  DepositCreate,
  Usage,
  UsageCreate,
  CustomerContextType,
  Store,
  User,
  Statistics,
  UseToastReturn
} from '../types';

interface CustomerProviderProps {
  children: ReactNode;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

export const useCustomer = (): CustomerContextType => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
};

export const CustomerProvider: React.FC<CustomerProviderProps> = ({ children }) => {
  const auth = useAuth();
  const { showToast } = useToast() as UseToastReturn;

  // AuthContextから取得
  const currentStore: Store | null = auth.store || null;
  const isAuthenticated = auth.isAuthenticated();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [usages, setUsages] = useState<Usage[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [statistics, setStatistics] = useState<Statistics>({
    storeId: 0,
    todayDeposits: 0,
    todayUsages: 0,
    todayPayments: 0,
    registeredCustomers: 0,
    date: new Date().toISOString().split('T')[0]
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const addCustomer = async (customerData: CustomerCreate): Promise<Customer> => {
    setError(null);
    setIsLoading(true);
    const startTime = Date.now();

    try {
      // 顧客登録試行をログ
      logger.userAction('customer_registration_attempt', {
        customerName: customerData.name,
        storeId: currentStore?.id,
        hasOptionalFields: {
          email: !!customerData.email,
          address: !!customerData.address,
          birthDate: !!customerData.birthDate,
          notes: !!customerData.notes
        },
        timestamp: new Date().toISOString()
      });

      // FastAPIに顧客データを送信
      const response = await customerApi.createCustomer(customerData);

      // 顧客の残高を取得（新規作成時は0のはずだが、一貫性のため）
      let currentBalance = 0;
      try {
        if (currentStore?.id) {
          const balanceResponse = await customerApi.getCustomerBalance(
            response.id,
            currentStore.id
          );
          currentBalance = balanceResponse.balance;
        }
      } catch (err) {
        console.warn(`Failed to fetch balance for new customer ${response.id}:`, err);
      }

      // APIレスポンスは既にcamelCaseに変換されているはず
      const formattedCustomer: Customer = {
        id: response.id,
        storeId: response.storeId,
        name: response.name,
        phone: response.phone,
        email: response.email ?? null,
        address: response.address ?? null,
        birthDate: response.birthDate ?? null,
        notes: response.notes ?? null,
        currentBalance: currentBalance,
        createdAt: response.createdAt || '',
        updatedAt: response.updatedAt || ''
      };

      setCustomers((prev) => [...prev, formattedCustomer]);

      const duration = Date.now() - startTime;

      // 顧客登録成功をログ
      logger.userAction('customer_registration_success', {
        customerId: formattedCustomer.id,
        customerName: formattedCustomer.name,
        storeId: formattedCustomer.storeId,
        timestamp: new Date().toISOString()
      });

      // パフォーマンスログ
      logger.performance('customer_registration', duration, {
        customerId: formattedCustomer.id
      });

      // 統計データを更新
      await fetchStatistics();

      return formattedCustomer;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage = handleApiError(error);
      setError(errorMessage);

      // 顧客登録失敗をログ
      logger.userAction('customer_registration_failed', {
        customerName: customerData.name,
        error: errorMessage,
        timestamp: new Date().toISOString()
      });

      // エラーログ
      logger.error('Customer registration failed', {
        context: 'customer',
        customerName: customerData.name,
        error: errorMessage,
        duration
      });

      logError(error, 'Failed to add customer');
      showToast(errorMessage, 'error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCustomer = async (id: number, customerData: CustomerUpdate): Promise<Customer> => {
    setError(null);
    setIsLoading(true);
    try {
      // FastAPIに顧客データを送信
      const updatedCustomer = await customerApi.updateCustomer(id, customerData);

      // ローカル状態を更新（APIレスポンスは既にcamelCaseに変換されている）
      const formattedCustomer: Customer = {
        id: updatedCustomer.id,
        storeId: updatedCustomer.storeId,
        name: updatedCustomer.name,
        phone: updatedCustomer.phone,
        email: updatedCustomer.email ?? null,
        address: updatedCustomer.address ?? null,
        birthDate: updatedCustomer.birthDate ?? null,
        notes: updatedCustomer.notes ?? null,
        currentBalance: getCustomerBalance(id),
        createdAt: updatedCustomer.createdAt || '',
        updatedAt: updatedCustomer.updatedAt || ''
      };

      setCustomers((prev) =>
        prev.map((customer) => (customer.id === id ? formattedCustomer : customer))
      );

      return formattedCustomer;
    } catch (error) {
      const errorMessage = handleApiError(error);
      setError(errorMessage);
      logError(error, 'Failed to update customer');
      showToast(errorMessage, 'error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const addDeposit = async (depositData: DepositCreate): Promise<Deposit> => {
    setError(null);
    setIsLoading(true);
    const startTime = Date.now();

    try {
      // 対象顧客情報を取得
      const targetCustomer = customers.find((c) => c.id === depositData.customerId);
      const previousBalance = getCustomerBalance(depositData.customerId);
      const anticipatedBalance = previousBalance + depositData.amount;

      // 入金処理開始をログ
      logger.userAction('deposit_attempt', {
        customerId: depositData.customerId,
        customerName: targetCustomer?.name,
        amount: depositData.amount,
        previousBalance,
        depositDate: depositData.depositDate,
        storeId: currentStore?.id,
        timestamp: new Date().toISOString()
      });

      // FastAPIに入金データを送信
      const newDeposit = await depositApi.createDeposit(depositData);

      // APIレスポンスを整形（不足フィールドはローカルで補完）
      const formattedDeposit: Deposit = {
        id: newDeposit.id,
        storeId: newDeposit.storeId,
        customerId: newDeposit.customerId,
        amount: newDeposit.amount,
        depositDate: newDeposit.depositDate,
        createdAt: newDeposit.createdAt,
        transactionId: newDeposit.transactionId ?? null,
        balanceAfter: newDeposit.balanceAfter ?? anticipatedBalance,
        notes: newDeposit.notes ?? null,
        customer: newDeposit.customer,
        user: newDeposit.user
      };

      // ローカル状態を更新
      setDeposits((prev) => [...prev, formattedDeposit]);

      const duration = Date.now() - startTime;
      const newBalance = formattedDeposit.balanceAfter ?? anticipatedBalance;

      // 入金成功をログ
      logger.userAction('deposit_success', {
        depositId: formattedDeposit.id,
        customerId: depositData.customerId,
        customerName: targetCustomer?.name,
        amount: depositData.amount,
        previousBalance,
        newBalance,
        depositDate: depositData.depositDate,
        storeId: formattedDeposit.storeId,
        timestamp: new Date().toISOString()
      });

      // パフォーマンスログ
      logger.performance('deposit_processing', duration, {
        depositId: formattedDeposit.id,
        amount: depositData.amount
      });

      // 統計データを更新
      await fetchStatistics();

      // 顧客の残高を更新
      await updateCustomerBalance(depositData.customerId);

      return formattedDeposit;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage = handleApiError(error);
      setError(errorMessage);

      // 入金失敗をログ
      logger.userAction('deposit_failed', {
        customerId: depositData.customerId,
        amount: depositData.amount,
        error: errorMessage,
        timestamp: new Date().toISOString()
      });

      // エラーログ
      logger.error('Deposit processing failed', {
        context: 'deposit',
        customerId: depositData.customerId,
        amount: depositData.amount,
        error: errorMessage,
        duration
      });

      logError(error, 'Failed to add deposit');
      showToast(errorMessage, 'error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const addUsage = async (usageData: UsageCreate): Promise<Usage> => {
    setError(null);
    setIsLoading(true);
    const startTime = Date.now();

    try {
      // 対象顧客情報を取得
      const targetCustomer = customers.find((c) => c.id === usageData.customerId);
      const previousBalance = getCustomerBalance(usageData.customerId);
      const anticipatedBalance = previousBalance - usageData.amount;

      // 精算処理開始をログ
      logger.userAction('payment_attempt', {
        customerId: usageData.customerId,
        customerName: targetCustomer?.name,
        amount: usageData.amount,
        previousBalance,
        usageDate: usageData.usageDate,
        storeId: currentStore?.id,
        timestamp: new Date().toISOString()
      });

      // FastAPIに利用データを送信
      const newUsage = await usageApi.createUsage(usageData);

      // APIレスポンスを整形（不足フィールドはローカルで補完）
      const formattedUsage: Usage = {
        id: newUsage.id,
        storeId: newUsage.storeId,
        customerId: newUsage.customerId,
        amount: newUsage.amount,
        usageDate: newUsage.usageDate,
        createdAt: newUsage.createdAt,
        transactionId: newUsage.transactionId ?? null,
        transactionTableId: newUsage.transactionTableId ?? undefined, // チケット使用API用
        balanceAfter: newUsage.balanceAfter ?? anticipatedBalance,
        notes: newUsage.notes ?? null,
        customer: newUsage.customer,
        user: newUsage.user
      };

      // ローカル状態を更新
      setUsages((prev) => [...prev, formattedUsage]);

      const duration = Date.now() - startTime;
      const newBalance = formattedUsage.balanceAfter ?? anticipatedBalance;

      // 精算成功をログ
      logger.userAction('payment_success', {
        usageId: formattedUsage.id,
        customerId: usageData.customerId,
        customerName: targetCustomer?.name,
        amount: usageData.amount,
        previousBalance,
        newBalance,
        usageDate: usageData.usageDate,
        storeId: formattedUsage.storeId,
        timestamp: new Date().toISOString()
      });

      // パフォーマンスログ
      logger.performance('payment_processing', duration, {
        usageId: formattedUsage.id,
        amount: usageData.amount
      });

      // 統計データを更新
      await fetchStatistics();

      // 顧客の残高を更新
      await updateCustomerBalance(usageData.customerId);

      return formattedUsage;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage = handleApiError(error);
      setError(errorMessage);

      // 精算失敗をログ
      logger.userAction('payment_failed', {
        customerId: usageData.customerId,
        amount: usageData.amount,
        error: errorMessage,
        timestamp: new Date().toISOString()
      });

      // エラーログ
      logger.error('Payment processing failed', {
        context: 'payment',
        customerId: usageData.customerId,
        amount: usageData.amount,
        error: errorMessage,
        duration
      });

      logError(error, 'Failed to add usage');
      showToast(errorMessage, 'error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 個別の顧客の残高を更新する関数
  const updateCustomerBalance = async (customerId: number): Promise<void> => {
    if (!currentStore?.id) return;

    try {
      const balanceResponse = await customerApi.getCustomerBalance(customerId, currentStore.id);

      setCustomers((prev) =>
        prev.map((customer) =>
          customer.id === customerId
            ? { ...customer, currentBalance: balanceResponse.balance }
            : customer
        )
      );

      // 選択中の顧客も更新
      if (selectedCustomer && selectedCustomer.id === customerId) {
        setSelectedCustomer((prev) =>
          prev ? { ...prev, currentBalance: balanceResponse.balance } : prev
        );
      }
    } catch (err) {
      console.error(`Failed to update balance for customer ${customerId}:`, err);
    }
  };

  const getCustomerBalance = useCallback(
    (customerId: number): number => {
      // キャッシュされた残高を返す（顧客オブジェクトに保存）
      if (!customers || customers.length === 0) {
        return 0;
      }
      const customer = customers.find((c) => c.id === customerId);
      return customer?.currentBalance ?? 0;
    },
    [customers]
  );

  const getCustomerDeposits = (customerId: number): Deposit[] => {
    const targetStoreId = currentStore?.id;

    return deposits
      .filter((deposit) => deposit.customerId === customerId && deposit.storeId === targetStoreId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  const getCustomerUsages = (customerId: number): Usage[] => {
    const targetStoreId = currentStore?.id;

    return usages
      .filter((usage) => usage.customerId === customerId && usage.storeId === targetStoreId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  const searchCustomers = (query: string): Customer[] => {
    const targetStoreId = currentStore?.id;
    let filteredCustomers = customers.filter((customer) => customer.storeId === targetStoreId);

    if (!query) return filteredCustomers;

    // 顧客検索をログ
    logger.userAction('customer_search', {
      searchQuery: query,
      storeId: targetStoreId,
      totalCustomers: filteredCustomers.length,
      timestamp: new Date().toISOString()
    });

    const results = filteredCustomers.filter(
      (customer) =>
        customer.name.includes(query) || (customer.phone && customer.phone.includes(query))
    );

    // 検索結果をログ
    logger.debug('Customer search results', {
      query,
      resultsCount: results.length,
      storeId: targetStoreId
    });

    return results;
  };

  const getCustomerTransactionHistory = (customerId: number): Array<Deposit | Usage> => {
    const deposits = getCustomerDeposits(customerId);
    const usages = getCustomerUsages(customerId);

    const allTransactions = [...deposits, ...usages];
    return allTransactions.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  };

  // 初期データを取得
  // Note: 個別のfetch関数ではsetIsLoadingを呼ばない（fetchAllDataで一元管理）
  const fetchCustomers = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !currentStore?.id) return;

    try {
      // 新しい最適化されたAPIを使用（残高込みで一括取得）
      const customersWithBalance = await customerApi.getCustomersWithBalances(currentStore.id);

      // データは既に適切な形式で返される（currentBalanceも含む）
      setCustomers(customersWithBalance);
    } catch (err) {
      handleApiError(err);
      logError(err, 'CustomerContext.fetchCustomers');
      logger.error('Failed to fetch customers:', { error: err });
      throw err; // Re-throw to let the wrapper handle it
    }
  }, [currentStore?.id, isAuthenticated]);

  // Note: 個別のfetch関数ではsetIsLoadingを呼ばない（fetchAllDataで一元管理）
  const fetchDeposits = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !currentStore?.id) return;

    try {
      const data = await depositApi.getDeposits();

      // APIレスポンスをDeposit型に変換（既にcamelCaseに変換されている）
      const formattedDeposits: Deposit[] = data.map((deposit) => ({
        id: deposit.id,
        storeId: deposit.storeId,
        customerId: deposit.customerId,
        amount: deposit.amount,
        depositDate: deposit.depositDate,
        createdAt: deposit.createdAt,
        transactionId: deposit.transactionId ?? null,
        balanceAfter: deposit.balanceAfter ?? null,
        notes: deposit.notes ?? null,
        customer: deposit.customer,
        user: deposit.user
      }));

      setDeposits(formattedDeposits);
    } catch (err) {
      handleApiError(err);
      logError(err, 'CustomerContext.fetchDeposits');
      throw err; // Re-throw to let the wrapper handle it
    }
  }, [currentStore?.id, isAuthenticated]);

  // Note: 個別のfetch関数ではsetIsLoadingを呼ばない（fetchAllDataで一元管理）
  const fetchUsages = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !currentStore?.id) return;

    try {
      const data = await usageApi.getUsages();

      // APIレスポンスをUsage型に変換（既にcamelCaseに変換されている）
      const formattedUsages: Usage[] = data.map((usage) => ({
        id: usage.id,
        storeId: usage.storeId,
        customerId: usage.customerId,
        amount: usage.amount,
        usageDate: usage.usageDate,
        createdAt: usage.createdAt,
        transactionId: usage.transactionId ?? null,
        balanceAfter: usage.balanceAfter ?? null,
        notes: usage.notes ?? null,
        customer: usage.customer,
        user: usage.user
      }));

      setUsages(formattedUsages);
    } catch (err) {
      handleApiError(err);
      logError(err, 'CustomerContext.fetchUsages');
      throw err; // Re-throw to let the wrapper handle it
    }
  }, [currentStore?.id, isAuthenticated]);

  // Note: 個別のfetch関数ではsetIsLoadingを呼ばない（fetchAllDataで一元管理）
  const fetchStatistics = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !currentStore?.id) return;

    try {
      const data = await statisticsApi.getStatistics(currentStore.id);
      // APIクライアントが自動的にsnake_caseをcamelCaseに変換済み
      setStatistics({
        storeId: data.storeId,
        todayDeposits: data.todayDeposits,
        todayUsages: data.todayUsages,
        todayPayments: data.todayPayments,
        registeredCustomers: data.registeredCustomers,
        date: data.date
      });
    } catch (err) {
      handleApiError(err);
      logError(err, 'CustomerContext.fetchStatistics');
      // APIエラー時はデフォルト値を設定
      setStatistics({
        storeId: currentStore.id,
        todayDeposits: 0,
        todayUsages: 0,
        todayPayments: 0,
        registeredCustomers: 0,
        date: new Date().toISOString().split('T')[0]
      });
      // Don't re-throw since we have a fallback
    }
  }, [currentStore?.id, isAuthenticated]);

  // 顧客選択のラッパー関数（ログ記録付き）
  const selectCustomer = (customer: Customer | null): void => {
    if (customer) {
      // 顧客選択をログ
      logger.userAction('customer_selected', {
        customerId: customer.id,
        customerName: customer.name,
        customerPhone: customer.phone,
        storeId: customer.storeId,
        currentBalance: getCustomerBalance(customer.id),
        timestamp: new Date().toISOString()
      });
    } else {
      // 顧客選択解除をログ
      logger.userAction('customer_deselected', {
        previousCustomerId: selectedCustomer?.id,
        timestamp: new Date().toISOString()
      });
    }
    setSelectedCustomer(customer);
  };

  // 全データを取得するラッパー関数
  // 重要: isLoadingの管理はこの関数でのみ行う（個別fetch関数では行わない）
  // これにより、並列実行時の競合状態を防ぐ
  const fetchAllData = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !currentStore?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      await Promise.all([fetchCustomers(), fetchDeposits(), fetchUsages(), fetchStatistics()]);
    } catch (err) {
      const errorMessage = 'データの取得に失敗しました。再度お試しください。';
      setError(errorMessage);
      logger.error('Failed to fetch all data:', { error: err });
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [
    fetchCustomers,
    fetchDeposits,
    fetchUsages,
    fetchStatistics,
    isAuthenticated,
    currentStore?.id,
    showToast
  ]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const value: CustomerContextType = {
    customers,
    deposits,
    usages,
    selectedCustomer,
    loading: isLoading,
    error,
    statistics,
    fetchCustomers,
    addCustomer,
    updateCustomer,
    selectCustomer,
    searchCustomers,
    fetchDeposits,
    fetchUsages,
    fetchStatistics,
    addDeposit,
    addUsage,
    getCustomerBalance,
    getCustomerDeposits,
    getCustomerUsages,
    getCustomerTransactionHistory,
    updateCustomerBalance
  };

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
};
